import { useMutation, useQueryClient } from "@tanstack/react-query";
import { storiesKeys } from "Api/Queries/Stories/useStoriesQuery";
import { storyPostUsageKeys } from "Api/Queries/Stories/useStoryPostUsageQuery";
import {
  type CreateStoryCommandResult,
  type CreateStoryRequest,
  postStories,
} from "Infrastructure/Api/Api";
import { processResponseWithApiProblemDetails } from "Infrastructure/Api/ApiUtils";

type Props = {
  onSuccess?: (result: CreateStoryCommandResult) => void;
  /** Receives the thrown response ({ data: ApiProblemDetails, status }) — inspect `data.errors` for typed failures. */
  onError?: (error: unknown) => void;
};

/**
 * Creates a story as a draft or, with `publish`, live in the same request (all-or-nothing on the
 * server). No feed invalidation: the stories feed is live SDK state — the author's own timeline
 * follows their story feed, so a published story arrives over the WebSocket.
 */
export const useCreateStoryMutation = ({ onSuccess, onError }: Props = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: processResponseWithApiProblemDetails(
      (request: CreateStoryRequest) => postStories(request),
    ),
    onSuccess: (result: CreateStoryCommandResult) => {
      queryClient.invalidateQueries({ queryKey: storiesKeys.all });
      queryClient.invalidateQueries({ queryKey: storyPostUsageKeys.all });
      onSuccess?.(result);
    },
    onError,
  });
};
